import { Trophy, Star, Award, Medal } from "lucide-react";
import { AvatarView } from "@/components/AvatarView";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell } from "recharts";

const RANK_COLORS = ["#FFD600", "#B0BEC5", "#FF8A65"];

export default function StatsTab({ students = [], groups = [] }) {
  const active = students.filter((s) => s.active_status !== "unsubscribed");
  const ranked = [...active].sort((a, b) => b.points - a.points);
  const total_points = active.reduce((a, s) => a + (s.points || 0), 0);
  const total_badges = active.reduce((a, s) => a + (s.badges || []).length, 0);
  const inGroup = (s, gid) => s.group_id === gid || (s.group_ids || []).includes(gid);
  const groupScores = groups.map((g) => {
    const gs = active.filter((s) => inGroup(s, g.id));
    return { id: g.id, name: g.name, color: g.color, points: gs.reduce((a, s) => a + (s.points || 0), 0), count: gs.length };
  }).sort((a, b) => b.points - a.points);

  const top = ranked.slice(0, 10);
  const chartData = top.map((s) => ({ name: s.name.split(" ")[0], points: s.points }));
  const data = { students: ranked, groups: groupScores, total_points, total_badges };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { icon: Star, label: "Puntos totales", value: data.total_points, color: "#00E5FF" },
          { icon: Award, label: "Medallas dadas", value: data.total_badges, color: "#FFD600" },
          { icon: Trophy, label: "Grupos", value: data.groups.length, color: "#AA00FF" },
          { icon: Medal, label: "Alumnos", value: data.students.length, color: "#FF007F" },
        ].map((s) => (
          <div key={s.label} className="bg-card rounded-2xl border border-border p-4 flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: s.color + "22", color: s.color }}><s.icon className="w-5 h-5" /></div>
            <div><div className="text-2xl font-display font-extrabold leading-none">{s.value}</div><div className="text-xs text-muted-foreground">{s.label}</div></div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-card rounded-2xl border border-border p-5">
          <h3 className="font-display font-bold mb-4 flex items-center gap-2"><Trophy className="w-5 h-5 text-yellow-500" /> Ranking de alumnos</h3>
          <div className="space-y-2">
            {top.map((s, i) => (
              <div key={s.id} data-testid={`rank-student-${s.id}`} className="flex items-center gap-3 p-2 rounded-xl hover:bg-muted">
                <span className="w-7 h-7 rounded-full flex items-center justify-center font-extrabold text-sm text-white" style={{ background: RANK_COLORS[i] || "hsl(var(--muted-foreground))" }}>{i + 1}</span>
                <AvatarView student={s} size={36} plain className="shrink-0" />
                <span className="flex-1 font-semibold text-sm truncate">{s.name}</span>
                <span className="font-extrabold text-primary">{s.points}</span>
              </div>
            ))}
            {top.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">Sin datos</p>}
          </div>
        </div>

        <div className="bg-card rounded-2xl border border-border p-5">
          <h3 className="font-display font-bold mb-4">Puntos por alumno</h3>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={chartData}>
                <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))" }} />
                <Bar dataKey="points" radius={[8, 8, 0, 0]}>
                  {chartData.map((_, i) => <Cell key={i} fill="#00E5FF" />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-muted-foreground text-center py-16">Sin datos todavía</p>}
        </div>

        <div className="bg-card rounded-2xl border border-border p-5 lg:col-span-2">
          <h3 className="font-display font-bold mb-4">Ranking de grupos</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.groups.map((g, i) => (
              <div key={g.id} data-testid={`rank-group-${g.id}`} className="flex items-center gap-3 p-3 rounded-xl border" style={{ borderColor: (g.color || "#888") + "55", background: (g.color || "#888") + "10" }}>
                <span className="w-7 h-7 rounded-full flex items-center justify-center font-extrabold text-sm text-white" style={{ background: RANK_COLORS[i] || "hsl(var(--muted-foreground))" }}>{i + 1}</span>
                <div className="flex-1"><div className="font-bold text-sm">{g.name}</div><div className="text-xs text-muted-foreground">{g.count} alumnos</div></div>
                <span className="font-extrabold">{g.points}</span>
              </div>
            ))}
            {data.groups.length === 0 && <p className="text-sm text-muted-foreground">Sin grupos</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
